import { useState, type FormEvent } from 'react';
import { Icon } from '~/components/ui';
import WhatsappPhoneInput from '~/components/WhatsappPhoneInput';
import { useT } from '~/i18n';
import { formatE164 } from '~/lib/englishAssessment';
import { isPhoneDraftEmpty, phoneDraftToE164 } from '~/lib/whatsappPhone';
import { EnglishClientError, saveWhatsappPhone } from '../api/englishClient';
import { useEnglishAssessments } from '../lib/useEnglishAssessments';

/**
 * «Tu WhatsApp» en Perfil (2026-10-08): el número que el alta pide (en las dos
 * puertas) y que acá se ve y se cambia. Vive en la membresía
 * (`whatsapp_phone_e164`); lo leemos del mismo GET de la evaluación de inglés,
 * que ya trae la barra del Perfil, y se guarda con su mismo PUT.
 */
export default function WhatsappPhoneCard() {
  const t = useT('onboarding').whatsapp;
  const { data, setData } = useEnglishAssessments();
  const current = data?.whatsapp_phone_e164 ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  if (!data) return null;

  const startEditing = () => {
    setDraft(current ?? '');
    setMessage(null);
    setEditing(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const e164 = phoneDraftToE164(draft);
    if (!e164) {
      setMessage({ kind: 'error', text: t.invalid });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const saved = await saveWhatsappPhone(e164);
      setData({ ...data, whatsapp_phone_e164: saved, whatsapp_phone_confirmed: true });
      setEditing(false);
      setMessage({ kind: 'ok', text: t.saved });
    } catch (err) {
      const invalid = err instanceof EnglishClientError && err.code === 'invalid_phone';
      setMessage({ kind: 'error', text: invalid ? t.invalid : t.failed });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" data-testid="whatsapp-card">
      <div className="card-body space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-brand mt-0.5">
            <Icon name="bubble" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="card-title text-lg">{t.label}</h2>
            <p className="text-sm hush">{t.consent}</p>
          </div>
        </div>

        {editing ? (
          <form onSubmit={submit} className="space-y-2">
            <WhatsappPhoneInput
              id="profile-whatsapp"
              value={draft}
              invalid={message?.kind === 'error'}
              disabled={busy}
              onChange={(value) => {
                setDraft(value);
                setMessage(null);
              }}
            />
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setEditing(false)}>
                {t.cancel}
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy || isPhoneDraftEmpty(draft)}>
                {busy && <span className="loading loading-spinner loading-xs" />}
                {t.save}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {current ? (
              <p className="font-semibold" data-testid="whatsapp-current">{formatE164(current)}</p>
            ) : (
              <p className="text-sm hush">{t.empty}</p>
            )}
            <button type="button" className={`btn btn-sm ${current ? 'btn-ghost' : 'btn-primary'}`} onClick={startEditing}>
              {current ? t.edit : t.add}
            </button>
          </div>
        )}

        {message && (
          <p className={`text-xs ${message.kind === 'ok' ? 'text-data' : 'text-friction'}`} role={message.kind === 'error' ? 'alert' : undefined}>
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
}
